"use client";

import * as React from "react";

import type { CheckboxProps } from "@radix-ui/react-checkbox";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { Label } from "@radix-ui/react-label";
import { CheckIcon } from "lucide-react";

import { cn } from "@repo/ui/lib/utils";

function Checkbox({
  className,
  forceMount,
  ...props
}: React.ComponentProps<typeof CheckboxPrimitive.Root> & { forceMount?: boolean }) {
  return (
    <CheckboxPrimitive.Root
      data-slot="checkbox"
      className={cn(
        "peer border-input dark:bg-input/30 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground dark:data-[state=checked]:bg-primary data-[state=checked]:border-primary focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive size-4 shrink-0 rounded-[4px] border shadow-xs transition-shadow outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      data-testid={`${props?.id}-chk`}
      {...props}
    >
      <CheckboxPrimitive.Indicator
        data-slot="checkbox-indicator"
        className="flex items-center justify-center text-current transition-none"
        data-state={props.checked ? "checked" : "unchecked"}
        {...(forceMount ? { forceMount: true } : {})}
      >
        <CheckIcon className="size-3.5" />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
}

function CheckboxWithLabel({
  id,
  children,
  checkboxClassName,
  labelClassName,
  labelTestId,
  forceMount = false,
  checked = false,
  ...props
}: React.ComponentProps<typeof CheckboxPrimitive.Root> & {
  id: string;
  labelClassName?: string;
  children: React.ReactNode;
  checkboxClassName?: string;
  labelTestId?: string;
  forceMount?: boolean;
  checked?: Pick<CheckboxProps, "checked">;
}) {
  return (
    <Label
      className={cn(
        "hover:bg-accent/50 flex items-start gap-3 rounded-lg border p-3 has-[[aria-checked=true]]:border-blue-600 has-[[aria-checked=true]]:bg-blue-50 dark:has-[[aria-checked=true]]:border-blue-900 dark:has-[[aria-checked=true]]:bg-blue-950",
        labelClassName,
      )}
      htmlFor={id}
      data-testid={labelTestId}
    >
      <Checkbox
        id={id}
        className={cn(
          "data-[state=checked]:border-[#162E51] data-[state=checked]:bg-[#162E51] data-[state=checked]:text-white dark:data-[state=checked]:border-blue-700 dark:data-[state=checked]:bg-blue-700 rounded-full text-white cursor-pointer",
          checkboxClassName,
        )}
        forceMount={forceMount}
        checked={checked}
        data-testid={`${labelTestId ?? id}-chk`}
        {...props}
      />
      {children}
    </Label>
  );
}

export { Checkbox, CheckboxWithLabel };
